import React from "react";
import {
    DISH_OPTIONS,
    EXTRACT_COLUMNS,
    displayValue,
    isNumericLike,
} from "./controller";

function ExtractTableCell({ controller, row, rowKey, sourceRowIndex, rowIndex, column, colIndex }) {
    const {
        selectedCell,
        editingCell,
        cellRefs,
        inputRef,
        changedCells,
        selectCell,
        startEdit,
        moveSelection,
        variantOptions,
        draftValue,
        setDraftValue,
        updateRowCell,
        stopEdit,
        saveEdit,
        focusCell,
    } = controller;

    const selected =
        selectedCell?.rowIndex === rowIndex &&
        selectedCell?.colIndex === colIndex;

    const editing =
        editingCell?.rowIndex === rowIndex &&
        editingCell?.colIndex === colIndex;

    const value = row?.[column];
    const numeric = isNumericLike(column, value);

    return (
        <td
            key={`${rowKey}-${column}`}
            ref={(el) => {
                cellRefs.current[`${rowIndex}-${colIndex}`] = el;
            }}
            tabIndex={0}
            className={`h-9 border border-gray-200 px-2 align-middle outline-none ${
                numeric ? "text-right" : "text-left"
            } ${
                changedCells[`${sourceRowIndex}-${column}`]
                    ? "bg-amber-50 text-amber-900"
                    : ""
            } ${
                selected
                    ? "relative z-10 bg-emerald-50 ring-2 ring-inset ring-[#01C37D]"
                    : ""
            }`}
            onClick={() => selectCell(rowIndex, colIndex)}
            onDoubleClick={() => startEdit(rowIndex, colIndex)}
            onKeyDown={(e) => {
                if (editing) return;

                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    moveSelection("up");
                } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    moveSelection("down");
                } else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    moveSelection("left");
                } else if (e.key === "ArrowRight") {
                    e.preventDefault();
                    moveSelection("right");
                } else if (e.key === "Tab") {
                    e.preventDefault();
                    moveSelection(e.shiftKey ? "left" : "right");
                } else if (e.key === "Enter") {
                    e.preventDefault();
                    startEdit(rowIndex, colIndex);
                } else if (e.key === "F2") {
                    e.preventDefault();
                    startEdit(rowIndex, colIndex);
                }
            }}
        >
            {editing ? (
                <EditableCell
                    controller={controller}
                    rowIndex={rowIndex}
                    colIndex={colIndex}
                    sourceRowIndex={sourceRowIndex}
                    column={column}
                    numeric={numeric}
                />
            ) : (
                <DisplayCell row={row} column={column} value={value} />
            )}
        </td>
    );
}

function EditableCell({ controller, rowIndex, colIndex, sourceRowIndex, column, numeric }) {
    const {
        inputRef,
        variantOptions,
        draftValue,
        setDraftValue,
        updateRowCell,
        stopEdit,
        saveEdit,
        focusCell,
    } = controller;

    if (column === "variantName") {
        return (
            <select
                ref={inputRef}
                className="h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none"
                value={draftValue || ""}
                onChange={(e) => {
                    setDraftValue(e.target.value);
                    const selectedVariant = variantOptions.find(
                        (option) => String(option.sizeId) === String(e.target.value),
                    );
                    updateRowCell(
                        sourceRowIndex,
                        column,
                        selectedVariant?.value || "",
                        {
                            variantId: selectedVariant?.sizeId || "",
                        },
                    );
                }}
                onBlur={() => stopEdit()}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        saveEdit("down");
                    } else if (e.key === "Tab") {
                        e.preventDefault();
                        saveEdit("right");
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        stopEdit();
                        setTimeout(() => focusCell(rowIndex, colIndex), 0);
                    }
                }}
            >
                <option value="">
                    {variantOptions.length ? "Select Variant" : "Select UOM first"}
                </option>

                {variantOptions.map((option) => (
                    <option
                        key={`${option.uomCode}-${option.sizeId}`}
                        value={option.sizeId}
                    >
                        {option.label} · {option.uomCode} · ID {option.sizeId}
                    </option>
                ))}
            </select>
        );
    }

    if (column === "iconDish") {
        return (
            <select
                ref={inputRef}
                className="h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none"
                value={draftValue || "N"}
                onChange={(e) => {
                    setDraftValue(e.target.value);
                    updateRowCell(sourceRowIndex, column, e.target.value);
                }}
                onBlur={() => stopEdit()}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        saveEdit("down");
                    } else if (e.key === "Tab") {
                        e.preventDefault();
                        saveEdit("right");
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        stopEdit();
                        setTimeout(() => focusCell(rowIndex, colIndex), 0);
                    }
                }}
            >
                {DISH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        );
    }

    return (
        <input
            ref={inputRef}
            className={`h-7 w-full rounded border border-[#01C37D] bg-white px-2 text-xs outline-none ${
                numeric ? "text-right" : "text-left"
            }`}
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onBlur={() => saveEdit()}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    saveEdit("down");
                } else if (e.key === "Tab") {
                    e.preventDefault();
                    saveEdit("right");
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    stopEdit();
                    setTimeout(() => focusCell(rowIndex, colIndex), 0);
                }
            }}
        />
    );
}

function DisplayCell({ row, column, value }) {
    return (
        <div
            className="truncate whitespace-nowrap leading-5"
            title={
                column === "variantName" && row?.variantId
                    ? `${displayValue(value)} / ID ${row.variantId}`
                    : displayValue(value)
            }
        >
            {column === "iconDish"
                ? value === "Y"
                    ? "Veg"
                    : "Non-Veg"
                : column === "variantName" && row?.variantId
                  ? `${displayValue(value)} / ID ${row.variantId}`
                  : displayValue(value)}
        </div>
    );
}

export default function ExtractCatalogueTable({ controller }) {
    const {
        visibleColumns,
        filteredRows,
        extracting,
    } = controller;

    return (
       <div className="max-h-[calc(100vh-170px)] overflow-auto">
            <table className="min-w-full table-fixed border-collapse text-xs">
                <thead className="sticky top-0 z-20 bg-[#f5f7fa] text-gray-700">
                    <tr>
                        {visibleColumns.map((column, colIndex) => (
                            <th
                                key={column}
                                className="h-9 whitespace-nowrap border border-gray-200 px-3 text-left font-semibold"
                                style={{ width: colIndex === 0 ? 190 : 170 }}
                                title={column}
                            >
                                <div className="flex min-w-0 items-center gap-1">
                                    <span className="truncate">{column}</span>
                                    <span className="shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-medium text-emerald-700">
                                        E
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {extracting ? (
                        <tr>
                            <td
                                colSpan={Math.max(visibleColumns.length, 1)}
                                className="px-4 py-16 text-center text-gray-500"
                            >
                                Extracting catalogue rows...
                            </td>
                        </tr>
                    ) : visibleColumns.length === 0 ? (
                        <tr>
                            <td className="px-4 py-16 text-center text-gray-400">
                                No columns selected. Use Select Columns or Reset.
                            </td>
                        </tr>
                    ) : filteredRows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={Math.max(visibleColumns.length, 1)}
                                className="px-4 py-16 text-center text-gray-400"
                            >
                                No extracted rows yet. Enter fileName, select UOM, select image, then click Done.
                            </td>
                        </tr>
                    ) : (
                        filteredRows.map(({ row, rowKey, sourceRowIndex }, rowIndex) => (
                            <tr
                                key={`${rowKey}-${sourceRowIndex}`}
                                className={rowIndex % 2 === 0 ? "bg-white" : "bg-[#fcfcfd]"}
                            >
                                {visibleColumns.map((column, colIndex) => (
                                    <ExtractTableCell
                                        key={`${rowKey}-${column}`}
                                        controller={controller}
                                        row={row}
                                        rowKey={rowKey}
                                        sourceRowIndex={sourceRowIndex}
                                        rowIndex={rowIndex}
                                        column={column}
                                        colIndex={colIndex}
                                    />
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
